import { useCallback, useRef } from "react"
import { requestSubscription } from "relay-runtime"
import { useUnmount } from "@/hooks/useUnmount"
import { deActivateComfortSignatureSubscription } from "@/modules/patients/detail/conclude/services/de/__generated__/deActivateComfortSignatureSubscription.graphql"
import { deActivateComfortSignatureSubscription as deActivateComfortSignatureSubscriptionNode } from "@/modules/patients/detail/conclude/services/de/deActivateComfortSignatureSubscription"
import { RelayEnvironment } from "@/relay-environment"

type ComfortSignatureActivationFailure = {
  errorType: string | null | undefined
  message: string | null | undefined
}

type ComfortSignatureActivationCallbacks = {
  onActivated: () => void
  onFailed: (failure: ComfortSignatureActivationFailure) => void
  onError: (error: Error) => void
}

// The activation blocks until the practitioner has entered the PIN at the card terminal, which takes longer than
// the GraphQL execution timeout allows, so it runs as a GraphQL subscription and yields exactly one
// event with the outcome. Only one activation runs at a time; starting another or unmounting disposes the current one
const useComfortSignatureActivation = () => {
  const activationSubscriptionRef = useRef<{ dispose: () => void } | null>(null)

  const cancelActivation = useCallback(() => {
    activationSubscriptionRef.current?.dispose()
    activationSubscriptionRef.current = null
  }, [])

  useUnmount(cancelActivation)

  const activateComfortSignature = useCallback(
    ({ onActivated, onFailed, onError }: ComfortSignatureActivationCallbacks) => {
      cancelActivation()
      activationSubscriptionRef.current = requestSubscription<deActivateComfortSignatureSubscription>(
        RelayEnvironment,
        {
          subscription: deActivateComfortSignatureSubscriptionNode,
          variables: {},
          onNext(subscriptionResponse) {
            cancelActivation()
            const activation = subscriptionResponse?.de_activateTelematikInfrastructureComfortSignature
            if (activation?.isActivated) {
              onActivated()
            } else {
              onFailed({ errorType: activation?.errorType, message: activation?.errorMessage })
            }
          },
          onError(subscriptionError) {
            cancelActivation()
            onError(subscriptionError)
          },
        },
      )
    },
    [cancelActivation],
  )

  return activateComfortSignature
}

export { useComfortSignatureActivation, type ComfortSignatureActivationFailure }
